/*
  Search overlay. Results come from the Predictive Search API, rendered by sections/sok-predictive-search.liquid.
  Opens from [data-sok-search-open], ⌘K / Ctrl+K and the sok:search event.
*/
if (!customElements.get('sok-search')) {
  // Lower-case and strip accents one character at a time, so match positions line up with the original text.
  const fold = (text) => [...text].map((ch) => ch.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().charAt(0) || ch).join('');

  class SokSearch extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[role="dialog"]');
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-sok-search-input]');
      this.clearButton = this.querySelector('[data-sok-search-clear]');
      this.body = this.querySelector('[data-sok-search-body]');
      this.popular = this.querySelector('[data-sok-search-popular]');
      this.results = this.querySelector('[data-sok-search-results]');
      this.foot = this.querySelector('[data-sok-search-foot]');
      this.count = this.querySelector('[data-sok-search-count]');
      this.cache = new Map();
      this.selected = -1;

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-sok-search-open]');
        if (!opener || event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        this.open(opener);
      });
      window.addEventListener('sok:search', () => this.open());
      document.addEventListener('keydown', (event) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          this.isOpen ? this.close() : this.open();
        } else if (event.key === 'Escape' && this.isOpen) {
          event.preventDefault();
          this.close();
        }
      });

      this.querySelectorAll('[data-sok-search-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.clearButton.addEventListener('click', () => {
        this.setQuery('');
        this.input.focus();
      });
      this.querySelectorAll('[data-sok-search-term]').forEach((chip) =>
        chip.addEventListener('click', () => {
          this.setQuery(chip.dataset.sokSearchTerm);
          this.input.focus();
        })
      );
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onKey(event));
      this.results.addEventListener('mousemove', (event) => {
        const row = event.target.closest('[data-sok-result]');
        if (row) this.select(this.rows().indexOf(row), false);
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      clearTimeout(this.hideTimer);
      this.hidden = false;
      document.body.classList.add('sok-lock');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          this.input.focus();
          this.input.select();
        })
      );
      if (typeof trapFocus === 'function') trapFocus(this.dialog, this.input);
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.body.classList.remove('sok-lock');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
      else this.opener?.focus?.();
      this.hideTimer = setTimeout(() => (this.hidden = true), 450);
    }

    setQuery(value) {
      this.input.value = value;
      this.onInput();
    }

    onInput() {
      const query = this.input.value.trim();
      this.clearButton.hidden = !this.input.value;
      this.body.classList.add('is-fading');
      clearTimeout(this.fadeTimer);
      this.fadeTimer = setTimeout(() => this.body.classList.remove('is-fading'), 90);

      clearTimeout(this.debounce);
      if (!query) {
        this.abort?.abort();
        this.render('', '');
        return;
      }
      this.debounce = setTimeout(() => this.fetchResults(query), 120);
    }

    async fetchResults(query) {
      const key = query.toLowerCase();
      if (this.cache.has(key)) {
        this.render(query, this.cache.get(key));
        return;
      }
      this.abort?.abort();
      this.abort = new AbortController();
      const params = new URLSearchParams({
        q: query,
        section_id: 'sok-predictive-search',
        'resources[type]': 'product,article,page',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][prefix]': 'last',
      });
      try {
        const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.abort.signal });
        if (!response.ok) throw new Error(response.status);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const markup = html.querySelector('[data-sok-search-results]')?.innerHTML || '';
        this.cache.set(key, markup);
        if (this.input.value.trim() === query) this.render(query, markup);
      } catch (e) {
        if (e.name !== 'AbortError') this.render(query, '');
      }
    }

    render(query, markup) {
      this.results.innerHTML = markup;
      this.popular?.toggleAttribute('hidden', Boolean(query));
      this.highlight(query);
      const total = this.rows().length;
      this.input.setAttribute('aria-expanded', String(total > 0));
      this.foot.hidden = total === 0;
      this.count.textContent = total === 1 ? this.dataset.resultOne : this.dataset.resultOther.replace('2', total);
      this.select(total ? 0 : -1, false);
    }

    highlight(query) {
      const needle = fold(query);
      if (!needle) return;
      this.results.querySelectorAll('[data-sok-result-name]').forEach((name) => {
        const text = name.textContent;
        const at = fold(text).indexOf(needle);
        if (at < 0) return;
        const mark = document.createElement('mark');
        mark.textContent = text.slice(at, at + needle.length);
        name.replaceChildren(text.slice(0, at), mark, text.slice(at + needle.length));
      });
    }

    rows() {
      return [...this.results.querySelectorAll('[data-sok-result]')];
    }

    select(index, scroll = true) {
      const rows = this.rows();
      rows.forEach((row, i) => {
        row.classList.toggle('is-selected', i === index);
        row.setAttribute('aria-selected', String(i === index));
      });
      this.selected = index;
      const row = rows[index];
      if (row) {
        this.input.setAttribute('aria-activedescendant', row.id);
        if (scroll) row.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKey(event) {
      const rows = this.rows();
      if (event.key === 'ArrowDown' && rows.length) {
        event.preventDefault();
        this.select(Math.min(this.selected + 1, rows.length - 1));
      } else if (event.key === 'ArrowUp' && rows.length) {
        event.preventDefault();
        this.select(Math.max(this.selected - 1, 0));
      } else if (event.key === 'Enter') {
        // Open the highlighted result; with none, fall through to the full results page.
        const row = rows[this.selected];
        if (row) {
          event.preventDefault();
          window.location.href = row.href;
        } else if (!this.input.value.trim()) {
          event.preventDefault();
        }
      }
    }
  }

  customElements.define('sok-search', SokSearch);
}
